import { createSlice, nanoid, PayloadAction } from '@reduxjs/toolkit';

import type { RootState } from '~redux/store';
import type { CartProduct, CartState } from '~types/cart';
import type { Participant, ParticipantOption } from '~types/participant';
import { stripTypename } from '~utils';

const initialState: CartState = {
  products: [],
};

type AddProductPayload = Omit<CartProduct, 'id'>;

interface RemoveParticipantPayload {
  productId: string;
  participantId: number;
}

const generateId = (): string => `cart_${nanoid()}`;

const normalizeOption = (option: ParticipantOption): ParticipantOption => ({
  ...stripTypename(option),
  timeslot: option.timeslot ? stripTypename(option.timeslot) : undefined,
});

const normalizeParticipant = (participant: Participant): Participant => ({
  ...participant,
  consumerCategory: stripTypename(participant.consumerCategory),
  options: participant.options.map(normalizeOption),
});

export const cartSlice = createSlice({
  initialState,
  name: 'cart',
  reducers: {
    addProduct: (state, action: PayloadAction<AddProductPayload>) => {
      const normalizedParticipants = action.payload.participants.map(normalizeParticipant);
      const existing = state.products.find(
        (product) =>
          product.productCategoryShortname === action.payload.productCategoryShortname &&
          product.validityCategoryShortname === action.payload.validityCategoryShortname &&
          product.date === action.payload.date &&
          product.contractorId === action.payload.contractorId,
      );

      if (existing) {
        const nextLocalId =
          existing.participants.reduce((max, participant) => Math.max(max, participant.id), 0) + 1;
        normalizedParticipants.forEach((participant, index) => {
          existing.participants.push({ ...participant, id: nextLocalId + index });
        });
        return;
      }

      state.products.push({
        ...action.payload,
        id: generateId(),
        participants: normalizedParticipants,
      });
    },
    clearCart: (state) => {
      state.products = [];
    },
    removeParticipant: (state, action: PayloadAction<RemoveParticipantPayload>) => {
      const product = state.products.find((product) => product.id === action.payload.productId);
      if (!product) return;
      product.participants = product.participants.filter(
        (participant) => participant.id !== action.payload.participantId,
      );
      if (product.participants.length === 0) {
        state.products = state.products.filter(
          (product) => product.id !== action.payload.productId,
        );
      }
    },
    removeProduct: (state, action: PayloadAction<string>) => {
      state.products = state.products.filter((product) => product.id !== action.payload);
    },
  },
});

export const { addProduct, clearCart, removeParticipant, removeProduct } = cartSlice.actions;

export const selectCartProducts = (state: RootState) => state.cart.products;
export const selectCartParticipantCount = (state: RootState) =>
  state.cart.products.reduce((sum, product) => sum + product.participants.length, 0);

export default cartSlice.reducer;
